import { supabase } from '@/integrations/supabase/client';
import { handleError } from '@/utils/handleError';
import { extractFunctionError, throwIfTaxAuthorityClosed, throwIfTaxConnectNotAllowed } from '@/utils/errorUtils';
import type { TaxGrantRecordedOutcome } from '@financialharmony/ui/tax/grantStage';
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

// caller-access-context.AC13 (issue #628) — see accountApiClient.ts for the rationale. src/ has
// no BFF hop, so the browser's own request already carries the real IP/UA that
// extractCallerContext forwards to the authority; this header declares the claim honestly
// rather than leaving it to extractCallerContext's stricter default.
const CALLER_ACCESS_MODE_HEADERS = { 'x-caller-access-mode': 'user-present' } as const;

/**
 * Why the reason is a union and not a boolean: each value implies a different next step for
 * the user, and collapsing them was a real bug in the client app — "granted, but not the role
 * we need" read as "access withdrawn", which sends someone who has just finished signing off
 * to look for a problem that does not exist.
 */
export type TaxBalanceUnavailableReason =
  | 'not_connected'
  | 'pending'
  | 'insufficient_scope'
  /** Active, but nothing read yet — not a withdrawal. */
  | 'not_readable'
  /**
   * The authority withdrew our access (scenario D, FIN-997). Can come WITH a balance: the last
   * one read, with its own date, never a current one.
   */
  | 'revoked'
  | 'incomplete_data'
  /**
   * Closed at the authority (scenario R). Can come WITH a balance: the figure is then the final
   * position, kept for the books, never a current one.
   */
  | 'closed'
  /**
   * Active, but the authority shows no tax account for the company yet (FIN-1453). Never a
   * figure and never a zero; the account stays connected and is checked again on a schedule.
   */
  | 'no_tax_account';

/** What the account's last balance read recorded besides a figure: it was seen, then vanished. */
type TaxBalanceNotice = 'vanished';

/** Shaped by the edge function; referenced only through TaxAccountView, so not exported. */
interface TaxAccountBalanceView {
  amount: string;
  interest?: string;
  currency: string;
  asOfDate: string;
  observedAt: string;
}

/** Mirrors the edge function's own view type. `balance` absent means no figure we can stand behind. */
export interface TaxAccountView {
  id: string;
  provider: string;
  countryCode: string;
  registrationNumber: string;
  status: string;
  /** The authority's name as users see it, from the authority registry (scenario Q). */
  authorityName: string;
  /** What the account is called in its jurisdiction, from the registry. */
  accountLabel: string;
  balance?: TaxAccountBalanceView;
  /**
   * A separate debt collected by Kronofogden, when the authority reports one. Never added to
   * `balance` — different bodies collect them, so a combined figure is not actionable.
   */
  enforcement?: { amount: string; currency: string } | null;
  balanceUnavailableReason?: TaxBalanceUnavailableReason;
  /**
   * The roles the authority last listed as live, as our role names. Null when the lookup has
   * never answered — "never asked" is not "granted nothing", which is an empty list (FIN-1303).
   */
  grantedRoles?: string[] | null;
  /**
   * The row is still loading (FIN-1301, FIN-1333): either the signer pressed "I've signed" and a
   * background check is confirming it (status `pending`), or that check just promoted the account
   * (status `active`) and its first balance read has not landed yet. The server derives it from
   * the check's deadline and a grace period after the check ended, so it clears on its own;
   * never re-derived in the browser.
   */
  activating?: boolean;
  /** When the loading ends (ISO): the check's deadline, then the grace period's end. Null unless `activating`. Bounds the list's refreshing. */
  activatingDeadline?: string | null;
  /** While activating: when the authority reopens (ISO), if its closure stopped the last check. */
  activatingClosedUntil?: string | null;
  /** When the authority was last asked about the balance (ISO). Null when never, or not reported. */
  lastCheckedAt?: string | null;
  /** When the schedule next asks (ISO). Null when unknown. */
  nextCheckAt?: string | null;
  /** `vanished`: a figure was read earlier and the authority now reports no account; the figure kept is the last one. */
  balanceNotice?: TaxBalanceNotice | null;
}

/**
 * The three fields FIN-1453 added, with "unknown" always `null`. Everything else is passed on as
 * the edge function sent it — including a reason this build does not know yet.
 */
function withCheckFields<T extends TaxAccountView>(account: T): T {
  return {
    ...account,
    lastCheckedAt: account.lastCheckedAt ?? null,
    nextCheckAt: account.nextCheckAt ?? null,
    balanceNotice: account.balanceNotice === 'vanished' ? 'vanished' : null,
  };
}

/** What `list` adds to each account: things only a row in the accounts list renders. */
export interface TaxAccountListItem extends TaxAccountView {
  /** When the last scheduled sync landed (ISO). Null when it has never synced. */
  syncedAt: string | null;
  /** How the latest trip to the authority ended; null when it never came back, or there was none. */
  grantOutcome: TaxGrantRecordedOutcome | null;
  /** Whether to offer discarding it. A hint only — the server re-decides on discard. */
  discardable: boolean;
}

/**
 * What a discard came to. `connected` is the server refusing because the account was connected
 * meanwhile — an answer for the user, not a fault.
 */
export type TaxDiscardOutcome = 'discarded' | 'connected';

/**
 * One settled transaction, with the evidence a normalised type is an interpretation of.
 * Referenced only through TaxAccountDetail, so not exported — same as TaxAccountBalanceView.
 */
interface TaxTransactionView {
  id: string;
  /** Null when withheld: the owner's private tax is never shown, to any viewer (FIN-1167 D10). */
  amount: string | null;
  amountWithheld?: boolean;
  currency: string;
  bookingDate: string | null;
  decisionDate: string | null;
  interestDate: string | null;
  transactionType: string;
  /** The authority's own wording. Shown verbatim for an `other` row rather than a guess. */
  rawLabel: string | null;
  description: string | null;
  /** FIN-769 S1: the row's suggestion (whatever its status), or why it is held. */
  bookkeeping?: {
    suggestionId: string | null;
    holdReason: string | null;
    /** When the hold began (ISO); null when the row is not held (FIN-1617). */
    heldAt?: string | null;
    /** A consultant's resolution of a held deposit or payout (FIN-1617). */
    resolution?: string | null;
  };
}

/** An announced item that has not yet moved the balance. Reached via TaxAccountDetail. */
interface TaxUpcomingItemView {
  id: string;
  /** Null when withheld: an announced charge that stays outside the books (FIN-1167 D10). */
  amount: string | null;
  amountWithheld?: boolean;
  /** The charge is the owner's own and never becomes a company payable (FIN-1167 AC13). */
  outsideBooks?: boolean;
  currency: string;
  dueDate: string | null;
  transactionType: string;
  rawLabel: string | null;
  description: string | null;
}

/**
 * The detail view (FIN-595).
 *
 * The balance lives on `account` and comes straight from the authority's saldo endpoint — it is
 * never derived from the transactions below it, and there is no ledger figure to compare it
 * against here (see the service for why).
 */
export interface TaxAccountDetail {
  account: TaxAccountView;
  /** The latest trip's outcome — what tells a cancelled setup from a signed one (FIN-1065). */
  grantOutcome: TaxGrantRecordedOutcome | null;
  transactions: TaxTransactionView[];
  upcoming: TaxUpcomingItemView[];
}

/** How a consultant resolves a deposit or payout held without a bank transaction (FIN-1617 D4). */
export type TaxHeldRowResolution = 'already_booked' | 'paid_from_outside';

/** `conflict`: the row changed since the page was read (already resolved another way, or no longer held). */
export type TaxHeldRowResolveOutcome = { status: 'resolved' } | { status: 'conflict'; message: string };

/** `tax_legal_form_profiles.account_holder`: the company itself, or its owner (FIN-1167). */
export type TaxAccountHolder = 'entity' | 'owner';

export interface TaxConnectContext {
  companyName: string;
  registrationNumber: string;
  authorityName: string;
  accountLabel: string;
  /** From the legal form's profile; null when it has none (the connect then refuses). */
  accountHolder: TaxAccountHolder | null;
  /** The server's answer to "may this company start a connect" (FIN-1640); false when absent. */
  canConnectTaxAccount: boolean;
  /** "allowed", "closed_for_legal_form" or "legal_form_unknown"; empty when absent, which reads as closed. */
  taxConnectReason: string;
  /** The company's client users, staff not counted (FIN-1335). */
  clientUserCount: number;
}

/**
 * Client for the `tax-account-api` edge function.
 *
 * Deliberately no new backend (FIN-716): the same function, service and RLS the client app
 * already uses. The connect flow can legitimately start in either app, so the only thing that
 * differs is which UI drives it.
 */
export class TaxAccountApiClient {
  /** Every tax account for the caller's active company. */
  static async list(): Promise<TaxAccountListItem[]> {
    const { data, error } = await supabase.functions.invoke('tax-account-api', {
      body: { action: 'list' },
      headers: CALLER_ACCESS_MODE_HEADERS,
    });
    if (error) throw error;
    return ((data?.accounts ?? []) as TaxAccountListItem[]).map(withCheckFields);
  }

  /**
   * The company we would prepare the permission for.
   *
   * Never an input: `companies.organization_number` is NOT NULL, so the server already holds
   * it, and accepting one would let a caller file another company's number.
   */
  static async connectContext(): Promise<TaxConnectContext> {
    try {
      const { data, error } = await supabase.functions.invoke('tax-account-api', {
        body: { action: 'connectContext' },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });
      if (error) throw error;
      const context = data as TaxConnectContext;
      return {
        ...context,
        accountHolder: context.accountHolder ?? null,
        clientUserCount: context.clientUserCount ?? 0,
        // Absent means not allowed: a connect is never offered on a missing answer (FIN-1640).
        canConnectTaxAccount: context.canConnectTaxAccount === true,
        taxConnectReason: context.taxConnectReason ?? '',
      };
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.couldNotReadTheCompanyDetails'), 'taxConnectContext');
      throw error;
    }
  }

  /**
   * Create the account if needed, then get the authority's pre-filled link.
   *
   * Returns the URL rather than redirecting: this tab has to stay put, because it is the one
   * that polls for the outcome.
   */
  static async startGrant(): Promise<{ account: TaxAccountView; grantUrl: string }> {
    try {
      const { data: started, error: startError } = await supabase.functions.invoke('tax-account-api', {
        body: { action: 'startConnect', countryCode: 'SE' },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });
      if (startError) throw startError;

      const { data, error } = await supabase.functions.invoke('tax-account-api', {
        // This app's own origin, so Skatteverket's popup comes back here (FIN-980). The backend
        // only matches its origin against the two app URLs it holds — same as consentApiClient.
        body: {
          action: 'startGrant',
          accountId: started.account.id,
          callbackUrl: `${window.location.origin}/tax/callback`,
        },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });
      if (error) throw error;
      return data as { account: TaxAccountView; grantUrl: string };
    } catch (error) {
      // Skatteverket's scheduled closure is not a fault: the dialog says when it reopens, inline,
      // and nothing is reported (FIN-1003). Nor is a connect refused for the company's legal form,
      // which the dialog states as the backend wrote it (FIN-1130, FIN-1167).
      const body = await throwIfTaxAuthorityClosed(error);
      throwIfTaxConnectNotAllowed(body);
      await handleError(error, translateOutsideReact('consultantErrors.fallback.couldNotStartTheConnection'), 'startTaxGrant', body);
      throw error;
    }
  }

  /**
   * Ask the authority which roles were actually signed for.
   *
   * A failure here is NOT a failed grant — the signature may have gone through perfectly and
   * the lookup simply be unreachable — so the caller keeps polling rather than giving up.
   *
   * `grantOutcome` is what the latest trip recorded (FIN-984/FIN-998): display-only, it tells
   * "signed, not live yet" and "cancelled" apart from "not back yet". `account.status` alone
   * says whether the grant is live.
   */
  static async checkGrant(
    accountId: string,
  ): Promise<{ account: TaxAccountView; grantOutcome: TaxGrantRecordedOutcome | null }> {
    const { data, error } = await supabase.functions.invoke('tax-account-api', {
      body: { action: 'checkGrant', accountId },
      headers: CALLER_ACCESS_MODE_HEADERS,
    });
    if (error) {
      await throwIfTaxAuthorityClosed(error);
      throw error;
    }
    const grantOutcome: TaxGrantRecordedOutcome | null = data.grantOutcome ?? null;
    return { account: withCheckFields(data.account as TaxAccountView), grantOutcome };
  }

  /**
   * "I've signed": record that the signer says they signed, so a background check promotes the
   * account without this tab open (FIN-1301). One fast write the dialog awaits and closes on only
   * when it succeeded, so a press that was never recorded never looks like activation.
   *
   * Idempotent on the server: a second press returns the account with the one check already open.
   * A failure, including the server refusing because the account is not waiting for a signature,
   * throws; the dialog words it inline, so nothing is toasted here.
   */
  static async confirmSigned(accountId: string): Promise<TaxAccountView> {
    const { data, error } = await supabase.functions.invoke('tax-account-api', {
      body: { action: 'confirmSigned', accountId },
      headers: CALLER_ACCESS_MODE_HEADERS,
    });
    if (error) throw error;
    return withCheckFields(data.account as TaxAccountView);
  }

  /**
   * Discard a setup that was never finished (scenarios C and N). The server decides whether the
   * account may go and never lets a connected one; that refusal comes back as `connected`.
   */
  static async discardDraft(accountId: string): Promise<TaxDiscardOutcome> {
    const { error } = await supabase.functions.invoke('tax-account-api', {
      body: { action: 'discardDraft', accountId },
      headers: CALLER_ACCESS_MODE_HEADERS,
    });
    if (!error) return 'discarded';
    // Decided by the service's own error code, never the bare status: the function's feature
    // gate answers 404 too, and that is not "already gone".
    const body: { error?: string } | null = await extractFunctionError(error);
    // Already gone — another tab discarded it first — is exactly what was asked for.
    if (body?.error === 'NOT_FOUND') return 'discarded';
    if (body?.error === 'CONFLICT') return 'connected';
    await handleError(error, translateOutsideReact('consultantErrors.fallback.couldNotDiscardThisSetup'), 'discardTaxDraft', body);
    throw error;
  }

  /** The single on-demand read in the whole feature; everything after is the scheduled sync. */
  static async refresh(accountId: string): Promise<TaxAccountView> {
    const { data, error } = await supabase.functions.invoke('tax-account-api', {
      body: { action: 'refresh', accountId },
      headers: CALLER_ACCESS_MODE_HEADERS,
    });
    if (error) {
      await throwIfTaxAuthorityClosed(error);
      throw error;
    }
    return withCheckFields(data.account as TaxAccountView);
  }

  /**
   * Everything the detail view renders for one account: the authority's own balance, and the
   * transactions and upcoming items behind it.
   *
   * One call rather than three, so the figure and the rows explaining it are read at the same
   * instant — a balance fetched a moment apart from its own transaction feed invites exactly
   * the "which of these is right" question the view exists to answer.
   */
  static async detail(accountId: string): Promise<TaxAccountDetail> {
    const { data, error } = await supabase.functions.invoke('tax-account-api', {
      body: { action: 'detail', accountId },
      headers: CALLER_ACCESS_MODE_HEADERS,
    });
    if (error) throw error;
    const detail = data as TaxAccountDetail;
    return { ...detail, account: withCheckFields(detail.account) };
  }

  /**
   * "This is a business item" on a private row (FIN-1167 EC8). Not supported yet: the server
   * records that it was asked (FIN-1336) and changes nothing. A failure is toasted and rethrown.
   */
  static async reportPrivateRowBusiness(transactionId: string): Promise<{ supported: boolean }> {
    const { data, error } = await supabase.functions.invoke('tax-account-api', {
      body: { action: 'reportPrivateRowBusiness', transactionId },
      headers: CALLER_ACCESS_MODE_HEADERS,
    });
    if (error) {
      const body = await extractFunctionError(error);
      await handleError(error, translateOutsideReact('consultantErrors.fallback.couldNotReportThisAsABusinessItem'), 'reportTaxPrivateRowBusiness', body);
      throw error;
    }
    return { supported: data?.supported === true };
  }

  /**
   * A consultant's resolution of a held deposit or payout (FIN-1617 AC10): "already booked" records
   * it with no entry, "paid from outside" sends it to Review. The note is mandatory; the server
   * refuses a client user and another company's consultant. A row that changed meanwhile comes back
   * as `conflict` for the dialog to say so; any other failure is toasted and rethrown.
   */
  static async resolveHeldRow(
    transactionId: string,
    resolution: TaxHeldRowResolution,
    note: string,
  ): Promise<TaxHeldRowResolveOutcome> {
    const { error } = await supabase.functions.invoke('tax-account-api', {
      body: { action: 'resolveHeldRow', transactionId, resolution, note },
      headers: CALLER_ACCESS_MODE_HEADERS,
    });
    if (!error) return { status: 'resolved' };
    const body: { error?: string; message?: string } | null = await extractFunctionError(error);
    if (body?.error === 'CONFLICT') {
      return { status: 'conflict', message: body.message ?? translateOutsideReact('consultantErrors.fallback.taxRowChanged') };
    }
    await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToResolveTaxRow'), 'resolveTaxHeldRow', body);
    throw error;
  }
}
